import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define the types for our step processor
export interface Step {
  id: string;
  name: string;
  isCompleted: boolean;
  isActive: boolean;
  component?: React.ReactNode;
}

interface StepProcessorContextType {
  steps: Step[];
  currentStepIndex: number;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (stepIndex: number) => void;
  completeStep: (stepId: string) => void;
  resetSteps: () => void;
  updateSteps: (newSteps: Step[]) => void;
}

const StepProcessorContext = createContext<StepProcessorContextType | undefined>(undefined);

interface StepProcessorProviderProps {
  children: ReactNode;
  initialSteps?: Step[];
}

export const StepProcessorProvider: React.FC<StepProcessorProviderProps> = ({ 
  children,
  initialSteps = []
}) => {
  const [steps, setSteps] = useState<Step[]>(initialSteps);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

  const goToNextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const goToStep = (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStepIndex(stepIndex);
    }
  };

  const completeStep = (stepId: string) => {
    setSteps(prevSteps => 
      prevSteps.map(step => 
        step.id === stepId 
          ? { ...step, isCompleted: true } 
          : step
      )
    );
  };

  const resetSteps = () => {
    setSteps(initialSteps);
    setCurrentStepIndex(0);
  };

  const updateSteps = (newSteps: Step[]) => {
    setSteps(newSteps);
  };

  // Update active step based on currentStepIndex
  React.useEffect(() => {
    setSteps(prevSteps => 
      prevSteps.map((step, index) => ({
        ...step,
        isActive: index === currentStepIndex
      }))
    );
  }, [currentStepIndex]);

  const value = {
    steps,
    currentStepIndex,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    completeStep,
    resetSteps,
    updateSteps
  };

  return (
    <StepProcessorContext.Provider value={value}>
      {children}
    </StepProcessorContext.Provider>
  );
};

export const useStepProcessor = (): StepProcessorContextType => {
  const context = useContext(StepProcessorContext);
  if (context === undefined) {
    throw new Error('useStepProcessor must be used within a StepProcessorProvider');
  }
  return context;
};
